<template>
	<div class="component-carousel-wrapper">
		<div class="component-carousel-viewport">
			<div class="component-container component-carousel">
				<slot />
			</div>
		</div>
		<div class="carousel-controls">
			<button class="prev-button" @click="onPrevClick">
				<i class="fas fa-arrow-left" />
			</button>
			<button class="next-button" @click="onNextClick">
				<i class="fas fa-arrow-right" />
			</button>
		</div>
		<div v-if="total_slides > 1" class="carousel-dots">
			<button
				v-for="n in total_slides"
				:key="n"
				class="dot"
				:class="{ 'dot-active': n === current_slide + 1 }"
				@click="$emit('update:currentSlide', n - 1)"
			/>
		</div>
	</div>
</template>

<script setup lang="ts">
	import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';

	const props = defineProps<{
		total_slides: number, // eslint-disable-line vue/prop-name-casing
		current_slide: number, // eslint-disable-line vue/prop-name-casing
		first_slide_delay?: number, // eslint-disable-line vue/prop-name-casing
	}>();

	const emit = defineEmits<{
		(update: 'update:currentSlide', value: number): void,
		(event: 'prev'): void,
		(event: 'next'): void,
	}>();

	const auto_slide_interval = ref<number | null>(null);
	const auto_slide_delay = ref(10_000); // Default auto-slide delay in milliseconds.

	const current_slide_delay = computed(() => props.current_slide === 0 && props.first_slide_delay ? props.first_slide_delay : auto_slide_delay.value);

	const onPrevClick = () => {
		emit('prev');
		resetAutoSlide();
	};

	const onNextClick = () => {
		emit('next');
		resetAutoSlide();
	};

	const startAutoSlide = () => {
		clearAutoSlide();
		auto_slide_interval.value = window.setInterval(() => {
			const nextSlide = (props.current_slide + 1) % props.total_slides;
			emit('update:currentSlide', nextSlide);
		}, current_slide_delay.value);
	};

	const clearAutoSlide = () => {
		if (auto_slide_interval.value !== null) {
			clearInterval(auto_slide_interval.value);
			auto_slide_interval.value = null;
		}
	};

	const resetAutoSlide = () => {
		startAutoSlide();
	};

	onBeforeUnmount(() => {
		clearAutoSlide();
	});

	watch(() => props.current_slide, () => {
		startAutoSlide();
	});

	onMounted(() => {
		if (props.current_slide < 0 || props.current_slide >= props.total_slides) {
			emit('update:currentSlide', 0);
		}
		startAutoSlide();
	});
</script>

<style lang="scss" scoped>
	@use '../assets/styles/base' as base;

	.component-carousel-wrapper {
		position: relative;
		width: 100%;
		height: 100%;

		.component-carousel-viewport {
			width: 100%;
			overflow: hidden;
			aspect-ratio: 4/3;

			@media screen and (max-width: 760px) {
				width: 300px;
			}
		}

		.prev-button {
			position: absolute;
			left: -16px;
			top: 50%;
			@media (max-width: 1200px) {
				top: 33%;
			}
			transform: translateY(-50%);
			width: 32px;
			height: 32px;
			background: rgba(white, 0.9);
			border: none;
			border-radius: 50%;
			box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
			font-size: 1.2rem;
			display: flex;
			align-items: center;
			justify-content: center;
			cursor: pointer;
			color: #333;
			opacity: 0;
			transition: opacity 0.3s ease;
			&:hover {
				background-color: white;
				box-shadow: rgba(white, 0.8) 0px 3px 8px;
				transition: box-shadow 0.2s ease;
			}
		}
		.next-button {
			position: absolute;
			right: -16px;
			top: 50%;
			@media (max-width: 1200px) {
				top: 33%;
			}
			transform: translateY(-50%);
			width: 32px;
			height: 32px;
			background: rgba(white, 0.9);
			border: none;
			border-radius: 50%;
			box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
			font-size: 1.2rem;
			display: flex;
			align-items: center;
			justify-content: center;
			cursor: pointer;
			color: #333;
			opacity: 0;
			transition: opacity 0.3s ease;
			&:hover {
				background-color: white;
				box-shadow: rgba(white, 0.8) 0px 3px 8px;
				transition: box-shadow 0.2s ease;
			}
		}
		&:hover {
			.prev-button,
			.next-button {
				opacity: 1;
			}
		}
	}

	.component-carousel {
		display: flex;
		align-items: center;
		border-radius: 0.5rem;
		overflow: hidden;
		position: relative;
		height: 100%;
		width: 100%;
	}

	.carousel-dots {
		position: absolute;
		top: -25px;
		display: flex;
		gap: 0.5rem;
		line-height: 0;

		.dot {
			width: 10px !important;
			height: 10px !important;
			border-radius: 50% !important;
			border: 0;
			padding: 0 !important;
			margin: 0 !important;
			box-sizing: content-box !important;
			font-size: 0 !important;
			line-height: 0 !important;
			background-color: #7084f4;
			display: inline-block;
			transition: background-color 0.3s ease;
			cursor: pointer;
			// box-shadow: 0 0 0 1px #ccc;
			vertical-align: middle;

			&.dot-active {
				background-color: white;
				@media (max-width: 1200px) {
					background-color: #b9b9b9;
				}
			}
		}
	}
</style>
